/**
 * Review Model
 * Represents customer reviews from Google Business Profile
 */

export interface Review {
  id: string;                      // Google review ID (unique identifier)
  authorName: string;              // Reviewer's display name
  authorPhotoUrl?: string | null;  // Reviewer's Google profile photo URL
  rating: number;                  // Star rating (1-5)
  text: string;                    // Review content/text
  relativeTimeDescription: string; // Human-readable time (e.g., "2 weeks ago")
  createdAt: Date;                 // Actual review timestamp
  source: 'google';                // Review source (Google Business Profile)
  googlePlaceId: string;           // Google Place ID for the business
  isAdminFeatured: boolean;        // Admin-selected for homepage display
  language?: string;               // Review language code
}

/**
 * Firestore Review Document
 * Includes Firestore timestamp handling
 */
export interface ReviewDocument extends Omit<Review, 'createdAt'> {
  createdAt: { seconds: number; nanoseconds: number } | Date | null;
}

/**
 * Google Places API Review Response
 * Raw structure from Google Places API (New)
 */
export interface GooglePlacesReview {
  name?: string;                   // Resource name (places/{placeId}/reviews/{reviewId})
  relativePublishTimeDescription?: string;
  rating?: number;
  text?: { text?: string; languageCode?: string };
  authorAttribution?: {
    displayName?: string;
    uri?: string;
    photoUri?: string;
  };
  publishTime?: string;            // ISO 8601 timestamp
}

/**
 * Google Places API Response for reviews
 */
export interface GooglePlacesReviewsResponse {
  reviews?: GooglePlacesReview[];
}

/**
 * Review sync status for admin UI
 */
export interface ReviewSyncStatus {
  lastSyncedAt: Date | null;
  totalReviews: number;
  featuredReviews: number;
  syncError?: string;
}

/**
 * Convert Firestore document to Review model
 */
export function reviewDocumentToReview(doc: ReviewDocument): Review {
  let createdAt: Date;
  
  if (doc.createdAt && typeof doc.createdAt === 'object' && 'seconds' in doc.createdAt) {
    // Firestore Timestamp
    createdAt = new Date(doc.createdAt.seconds * 1000);
  } else if (doc.createdAt instanceof Date) {
    createdAt = doc.createdAt;
  } else {
    createdAt = new Date();
  }

  return {
    ...doc,
    createdAt
  } as Review;
}